import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import useSetupProgress from '../hooks/useSetupProgress';
import useHomeKpis from '../hooks/useHomeKpis';

const SEEN_COMPLETE_KEY = 'zrf_home_seen_complete';

const SETUP_STEPS = [
  {
    key: 'property',
    stepKey: 'property',
    navigateTo: 'properties',
    title: 'Add a property and unit',
    description:
      'Create at least one property with one unit. Most features need a place to attach tenants and work orders.',
    icon: <HomeWorkOutlinedIcon color="primary" />,
  },
  {
    key: 'tenant',
    stepKey: 'tenant',
    navigateTo: 'tenants',
    title: 'Add a tenant',
    description:
      'Record who is renting. You will link this tenant to a lease in the next step.',
    icon: <PersonOutlinedIcon color="primary" />,
  },
  {
    key: 'lease',
    stepKey: 'lease',
    navigateTo: 'leases',
    title: 'Create a lease',
    description:
      'Connect a tenant to a property and unit with lease dates and rent details.',
    icon: <DescriptionOutlinedIcon color="primary" />,
  },
];

const DASHBOARD_PREVIEW_TILES = [
  {
    key: 'maintenance',
    title: 'Recent maintenance',
    copy: 'Open work orders will show here after setup — so Tuesday chaos stays visible.',
    icon: <BuildOutlinedIcon color="disabled" />,
  },
  {
    key: 'leasesEnding',
    title: 'Leases ending',
    copy: 'Renewals in the next 60 days will land here after your first lease.',
    icon: <EventAvailableOutlinedIcon color="disabled" />,
  },
  {
    key: 'messages',
    title: 'Tenant messages',
    copy: 'Threads that need a reply will show here after you add a tenant.',
    icon: <MessageOutlinedIcon color="disabled" />,
  },
];

function StepRow({ step, done, partial, isNext, onGo }) {
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        borderColor: isNext && !done ? 'primary.main' : undefined,
        boxShadow: isNext && !done ? 1 : 0,
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ pt: 0.5 }}>
            {done ? (
              <CheckCircleIcon color="success" />
            ) : (
              <RadioButtonUncheckedIcon color={isNext ? 'primary' : 'disabled'} />
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              {step.icon}
              <Typography variant="subtitle1" fontWeight={600}>
                {step.title}
              </Typography>
              {done && <Chip label="Done" size="small" color="success" />}
              {partial && !done && (
                <Chip label="Add a unit" size="small" color="warning" />
              )}
              {isNext && !done && <Chip label="Next" size="small" color="primary" />}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: done ? 0 : 2 }}>
              {step.description}
            </Typography>
            {!done && isNext && (
              <Button variant="contained" size="small" onClick={onGo}>
                Go to{' '}
                {step.navigateTo === 'properties'
                  ? 'Properties'
                  : step.navigateTo === 'tenants'
                    ? 'Tenants'
                    : 'Leases'}
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function DashboardPreview() {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        After setup
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Preview of your Home dashboard — live counts appear here once property, tenant, and lease
        are in place (use the checklist above).
      </Typography>
      <Grid container spacing={2}>
        {DASHBOARD_PREVIEW_TILES.map((tile) => (
          <Grid item xs={12} sm={4} key={tile.key}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                bgcolor: 'action.hover',
                opacity: 0.9,
                borderStyle: 'dashed',
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <LockOutlinedIcon fontSize="small" color="disabled" />
                  {tile.icon}
                  <Typography variant="subtitle2" color="text.secondary">
                    {tile.title}
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  color="text.disabled"
                  sx={{ lineHeight: 1.1, mb: 0.5 }}
                >
                  —
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tile.copy}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function KpiStrip({ kpis, onNavigate }) {
  const tiles = [
    {
      key: 'maintenance',
      label: 'Open maintenance',
      value: kpis.openMaintenance,
      tab: 'maintenance',
      icon: <BuildOutlinedIcon color="primary" />,
      hint: kpis.openMaintenance === 0 ? 'None open' : 'Needs attention',
    },
    {
      key: 'leasesEnding',
      label: 'Leases ending (60d)',
      value: kpis.leasesEndingSoon,
      tab: 'leases',
      icon: <EventAvailableOutlinedIcon color="primary" />,
      hint: kpis.leasesEndingSoon === 0 ? 'None soon' : 'Review renewals',
    },
    {
      key: 'activeLeases',
      label: 'Active leases',
      value: kpis.activeLeases,
      tab: 'leases',
      icon: <DescriptionOutlinedIcon color="primary" />,
      hint: 'Portfolio pulse',
    },
    {
      key: 'messages',
      label: 'Messages',
      value: null,
      tab: 'messaging',
      icon: <MessageOutlinedIcon color="primary" />,
      hint: 'Check tenant threads',
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Needs attention
      </Typography>
      {kpis.loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={28} />
        </Box>
      )}
      {kpis.error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {kpis.error}
        </Alert>
      )}
      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid item xs={12} sm={6} md={3} key={tile.key}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardActionArea onClick={() => onNavigate(tile.tab)} sx={{ height: '100%' }}>
                <CardContent>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    {tile.icon}
                    <Typography variant="body2" color="text.secondary">
                      {tile.label}
                    </Typography>
                  </Stack>
                  <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.1 }}>
                    {tile.value == null ? '—' : tile.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {tile.hint}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function SetupChecklist({ steps, nextStepKey, onNavigate }) {
  return (
    <>
      {SETUP_STEPS.map((step) => (
        <StepRow
          key={step.key}
          step={step}
          done={steps[step.stepKey]?.done}
          partial={steps[step.stepKey]?.partial}
          isNext={nextStepKey === step.stepKey}
          onGo={() => onNavigate(step.navigateTo)}
        />
      ))}
    </>
  );
}

/**
 * Landlord Home: required onboarding first, then KPI dashboard (ZRF-HOME-01 / RES-14).
 * Incomplete accounts cannot skip setup; complete accounts keep setup reopenable.
 */
export default function HomeDashboard({ onNavigate }) {
  const {
    loading,
    error,
    refresh,
    leases,
    steps,
    allComplete,
    completedCount,
    totalSteps,
    nextStepKey,
    homePhase,
  } = useSetupProgress();

  const kpis = useHomeKpis({ enabled: allComplete, leases });

  const [returningUser, setReturningUser] = useState(false);

  useEffect(() => {
    if (!allComplete) {
      setReturningUser(false);
      try {
        localStorage.removeItem(SEEN_COMPLETE_KEY);
      } catch {
        /* ignore */
      }
      return;
    }
    try {
      setReturningUser(localStorage.getItem(SEEN_COMPLETE_KEY) === '1');
    } catch {
      setReturningUser(false);
    }
  }, [allComplete]);

  // Mark complete seen after first celebration session — do not flip UI mid-session
  useEffect(() => {
    if (!allComplete || returningUser) return undefined;
    const timer = window.setTimeout(() => {
      try {
        localStorage.setItem(SEEN_COMPLETE_KEY, '1');
      } catch {
        /* ignore */
      }
    }, 2500);
    return () => window.clearTimeout(timer);
  }, [allComplete, returningUser]);

  const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;
  const showCelebration = allComplete && !returningUser;

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HomeOutlinedIcon />
        Home
      </Typography>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button onClick={refresh}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!loading && !error && !allComplete && (
        <>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {homePhase === 'partial'
              ? 'One step left — finish setup to unlock your ops overview.'
              : 'Complete these steps to start using ZenRentFlow. Each step unlocks more of the app.'}
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Setup progress
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {completedCount} of {totalSteps} complete
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>

          <Typography variant="h6" gutterBottom>
            Setup checklist
          </Typography>

          <SetupChecklist steps={steps} nextStepKey={nextStepKey} onNavigate={onNavigate} />

          <DashboardPreview />
        </>
      )}

      {!loading && !error && allComplete && (
        <>
          {showCelebration && (
            <Alert severity="success" sx={{ mb: 3 }}>
              You&apos;re all set. Your property, tenant, and lease are in place — here is your week at a glance.
            </Alert>
          )}

          <KpiStrip kpis={kpis} onNavigate={onNavigate} />

          <Typography variant="h6" gutterBottom>
            Quick actions
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Maintenance', tab: 'maintenance', icon: <BuildOutlinedIcon /> },
              { label: 'Messaging', tab: 'messaging', icon: <MessageOutlinedIcon /> },
              { label: 'Documents', tab: 'documents', icon: <FolderOutlinedIcon /> },
            ].map((item) => (
              <Grid item xs={12} sm={4} key={item.tab}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={item.icon}
                  onClick={() => onNavigate(item.tab)}
                  sx={{ py: 2, justifyContent: 'flex-start', pl: 2 }}
                >
                  {item.label}
                </Button>
              </Grid>
            ))}
          </Grid>

          <Accordion disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Setup complete · view steps</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <SetupChecklist steps={steps} nextStepKey={null} onNavigate={onNavigate} />
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {!loading && !error && (
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              refresh();
              if (allComplete) kpis.refresh();
            }}
          >
            Refresh
          </Button>
        </Stack>
      )}
    </Box>
  );
}
